'use client'

import {useMemo, useState} from 'react'
import {Send, ShieldAlert} from 'lucide-react'
import type {Account, Contact, ContactLabel, ReplyPayload} from '@/types'
import {apiPost} from '@/lib/api'
import {emptyReplyPayload, payloadText} from '@/lib/utils'
import {resolveTargets} from '@/lib/targetResolution'
import {PageHeader} from '@/components/PageHeader'
import {ReplyPayloadEditor} from '@/components/ReplyPayloadEditor'
import {ContactSelectorPanel} from '@/components/ContactSelectorPanel'
import {message} from '@/components/Toast'

export function MassMessageView({account, contacts, labels}: { account: Account | null; contacts: Contact[]; labels: ContactLabel[] }) {
    const [selected, setSelected] = useState<string[]>([])
    const [selectedLabelIds, setSelectedLabelIds] = useState<number[]>([])
    const [payload, setPayload] = useState<ReplyPayload>(emptyReplyPayload())
    const [sending, setSending] = useState(false)

    const resolvedTargets = useMemo(
        () => resolveTargets({contactWxids: selected, labelIds: selectedLabelIds, contacts}),
        [selected, selectedLabelIds, contacts],
    )

    const canSend = !!account && resolvedTargets.length > 0 && !!payloadText(payload).trim() && !sending

    const addContact = (wxid: string) => {
        setSelected((prev) => (prev.includes(wxid) ? prev : [...prev, wxid]))
    }
    const removeContact = (wxid: string) => {
        setSelected((prev) => prev.filter((item) => item !== wxid))
    }
    const addLabel = (id: number) => {
        setSelectedLabelIds((prev) => (prev.includes(id) ? prev : [...prev, id]))
    }
    const removeLabel = (id: number) => {
        setSelectedLabelIds((prev) => prev.filter((item) => item !== id))
    }

    async function sendMass() {
        if (!account || !canSend) return
        setSending(true)
        try {
            await apiPost('/messages/send', {
                wxid: account.wxid,
                targets: resolvedTargets,
                type: payload.type || 'text',
                payload,
                content: payload.content,
            })
            setPayload(emptyReplyPayload(payload.type || 'text'))
            message.success('群发完成')
        } finally {
            setSending(false)
        }
    }

    return (
        <section className="flex flex-col h-full bg-page">
            <PageHeader title="群发消息"/>
            <div className="flex-1 flex flex-col md:flex-row overflow-y-auto md:overflow-hidden p-3 md:p-6 gap-3 md:gap-6">
                <ContactSelectorPanel
                    contacts={contacts}
                    selectedWxids={selected}
                    onSelect={addContact}
                    onRemove={removeContact}
                    onClear={() => { setSelected([]); setSelectedLabelIds([]) }}
                    labels={labels}
                    selectedLabelIds={selectedLabelIds}
                    onLabelSelect={addLabel}
                    onLabelRemove={removeLabel}
                />
                <div className="flex-1 bg-white rounded-card shadow-card border border-hair flex flex-col h-full overflow-hidden">
                    <div className="p-4 border-b border-hair bg-page flex justify-between items-center shrink-0">
                        <div className="text-sm text-ink">
                            已选择:
                            <span className="text-accent font-bold text-base px-1">{resolvedTargets.length}</span>
                            位联系人
                        </div>
                        <div className="text-xs text-muted flex items-center gap-1">
                            <ShieldAlert size={14}/>
                            <span>防风控提示：单次群发建议不超过 200 人</span>
                        </div>
                    </div>
                    <div className="flex-1 overflow-auto p-5">
                        <ReplyPayloadEditor
                            payload={payload}
                            onChange={setPayload}
                            action={
                                <button
                                    type="button"
                                    className="btn-primary"
                                    disabled={!canSend}
                                    onClick={sendMass}
                                >
                                    <Send size={16}/>
                                    {sending ? '发送中' : '开始群发'}
                                </button>
                            }
                        />
                    </div>
                </div>
            </div>
        </section>
    )
}
